// Olene sign-in copy and Cognito error mapping.
// Copy is fixed by olene-platform/docs/signin.md; do not reword it here.
// Raw AWS error text is never shown to the user.

export const APP_NAME = "PayClear";

export const MSG = {
  badCredentials: "Email/username or password is incorrect.",
  notConfirmed: "This account hasn't been activated yet. Check your email for your invitation.",
  tooMany: "Too many attempts. Please wait a few minutes and try again.",
  badCode: "That code is incorrect or has expired.",
  passwordRules: "At least 8 characters, with upper and lower case letters, a number and a symbol.",
  generic: "Sign-in failed. Please try again.",
  resetRequired: "Your password needs to be reset. We've sent a code to your email.",
  resetSent: "If an account exists for that email or username, we sent a code to its email address.",
  mismatch: "The passwords don't match.",
  // Added after spec: most older accounts' usernames aren't their email, and the pool can't sign
  // in by email (olene-platform plan §5). Shown only when an email was typed; identical whether or
  // not the account exists, so it reveals nothing.
  usernameHint: "Older accounts sign in with their username, not their email. Forgot it? Choose “Forgot username?”.",
  usernameSent: "If an account uses that email, we sent its username there.",
  noAccess: `Your Olene account works, but it doesn't have access to ${APP_NAME} yet. Ask an administrator to add you.`,
  // PayClear-only (self sign-up on the prod pool).
  signUpClosed: "New accounts are created by an administrator.",
  accountExists: "An account with this email already exists. Sign in instead.",
  signUpFailed: "We couldn't create your account. Please try again.",
};

const errorName = (err) => (err && (err.code || err.name)) || "";

// Cognito answers a sign-up on a pool with AllowAdminCreateUserOnly=true with
// NotAuthorizedException "SignUp is not permitted for this user pool".
export function isSignUpNotPermitted(err) {
  return errorName(err) === "NotAuthorizedException" && /sign ?up is not permitted/i.test(err?.message || "");
}

// Maps any Cognito error to the plain message the user sees.
export function messageFor(err) {
  switch (errorName(err)) {
    case "NotAuthorizedException":
    case "UserNotFoundException":
      return MSG.badCredentials;
    case "UserNotConfirmedException":
      return MSG.notConfirmed;
    case "LimitExceededException":
    case "TooManyRequestsException":
    case "TooManyFailedAttemptsException":
      return MSG.tooMany;
    case "CodeMismatchException":
    case "ExpiredCodeException":
      return MSG.badCode;
    case "InvalidPasswordException":
      return MSG.passwordRules;
    default:
      return MSG.generic;
  }
}

// Same mapping for the create-account flow, plus the sign-up-only cases.
export function signUpMessageFor(err) {
  if (isSignUpNotPermitted(err)) return MSG.signUpClosed;
  switch (errorName(err)) {
    case "UsernameExistsException":
    case "AliasExistsException":
      return MSG.accountExists;
    case "InvalidPasswordException":
    case "LimitExceededException":
    case "TooManyRequestsException":
    case "TooManyFailedAttemptsException":
    case "CodeMismatchException":
    case "ExpiredCodeException":
      return messageFor(err);
    default:
      return MSG.signUpFailed;
  }
}

// The sign-in error, with the username hint when the person typed an email.
export function signInMessageFor(err, identifier) {
  const msg = messageFor(err);
  return msg === MSG.badCredentials && (identifier || "").includes("@") ? `${msg} ${MSG.usernameHint}` : msg;
}

// The "code sent" notice, with the same hint (a code only goes out if the name matched).
export function resetSentFor(identifier) {
  return (identifier || "").includes("@") ? `${MSG.resetSent} ${MSG.usernameHint}` : MSG.resetSent;
}

// Trim; lower-case only when it looks like an email. Usernames stay as typed.
export function normalizeIdentifier(value) {
  const v = (value || "").trim();
  return v.includes("@") ? v.toLowerCase() : v;
}
